import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { BackLink, recordGate } from '@/components/record';
import ActivityDialog from '@/components/admin/ActivityDialog';
import { ACTIVITY_STYLES } from '@/shared/constants';
import { ACTIVITY_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/activity/:id', layout: 'owner', access: "profile === 'Admin_c'" };

export default function AdminActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: entry, loading, error, run } = useFetch(
    () => sdk.table('activity_c').select(ACTIVITY_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/activity" label="Back to Activity" />;
  const gate = recordGate({ back, loading, record: entry, error, run, what: 'Entry' });
  if (gate) return gate;

  async function remove() {
    const res = await sdk.table('activity_c').remove(entry.Id);
    if (!committed(res, 'Entry deleted')) return false;
    navigate('/admin/activity');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow={`Activity · ${formatRelativeTime(entry.CreatedOn)}`}
        title={entry.Name}
        description={entry.target_c || undefined}
        actions={
          <>
            <StatusBadge value={entry.action_c} styles={ACTIVITY_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete title="Delete this entry?" description="It leaves the audit trail for good." onConfirm={remove} />
          </>
        }
      />

      <Panel title="Entry" icon="History">
        <dl className="divide-y divide-border">
          <DetailRow label="What happened">{entry.Name}</DetailRow>
          <DetailRow label="Area">
            <StatusBadge value={entry.action_c} styles={ACTIVITY_STYLES} />
          </DetailRow>
          <DetailRow label="Applies to">{entry.target_c && <span className="font-mono text-sm">{entry.target_c}</span>}</DetailRow>
          <DetailRow label="Detail">{entry.detail_c && <span className="whitespace-pre-wrap">{entry.detail_c}</span>}</DetailRow>
          <DetailRow label="When">{formatLocalDate(entry.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
        </dl>
      </Panel>

      <ActivityDialog open={editOpen} onOpenChange={setEditOpen} entry={entry} onSaved={run} />
    </div>
  );
}
